import { Link, useNavigate } from 'react-router-dom';
import { useSession, useContent } from '../hooks/useSession';
import { countRead, nextUp, totalLessons, trackMinutes, lessonKey } from '../lib/curriculum';
import { Icon } from '../components/Icon';

/** The service standards every vendor is held to. Stated up front, not buried in a lesson. */
const SLAS = [
  { k: 'Pack an in-stock order within', v: '48 hours' },
  { k: 'Fulfilment rate', v: '98%+' },
  { k: 'Quality rate, no disputes', v: '97%+' },
  { k: 'Rating to qualify for picks', v: '4.3+' },
];

const TOOL_CARDS = [
  { to: '/tools/grader', name: 'Listing quality checker', blurb: 'Score a live listing out of 100 and see exactly which field to fix first.', path: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' },
  { to: '/tools/calculator', name: 'Pricing and margin', blurb: 'What you keep per unit, what the buyer pays, and whether the next band is worth it.', path: 'M9 7h6m-6 4h6m-6 4h2M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z' },
  { to: '/tools/photos', name: 'Photo standard', blurb: 'Eight rules, pass and reject side by side, with slots for your own shots.', path: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z' },
  { to: '/checklists', name: 'Four checklists', blurb: 'Listing, dispatch and QC, festival readiness, monthly review. Printable.', path: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01' },
];

/** Progress ring for the hero. An SVG arc reads better than a bar against the dark ground. */
function Ring({ pct }: { pct: number }) {
  const r = 62;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ position: 'relative', width: 150, height: 150 }}>
      <svg width="150" height="150" viewBox="0 0 150 150" aria-hidden="true">
        <circle cx="75" cy="75" r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="10" />
        <circle
          cx="75" cy="75" r={r} fill="none"
          stroke="var(--brand-primary)" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)}
          transform="rotate(-90 75 75)"
          style={{ transition: 'stroke-dashoffset .4s ease' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeContent: 'center', textAlign: 'center' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 800, color: '#fff', lineHeight: 1 }}>
          {pct}%
        </div>
        <div style={{ fontSize: 'var(--fs-2xs)', letterSpacing: '.12em', color: 'rgba(255,255,255,.65)', marginTop: 4 }}>
          COMPLETE
        </div>
      </div>
    </div>
  );
}

export function Dashboard() {
  const content = useContent();
  const { progress } = useSession();
  const navigate = useNavigate();

  const read = countRead(content.tracks, progress.done);
  const total = totalLessons(content.tracks);
  const next = nextUp(content.tracks, progress.done);
  const pct = total ? Math.round((read / total) * 100) : 0;

  return (
    <div className="vu-wrap">
      <section className="vu-hero">
        <div className="vu-hero-body">
          <div className="vu-hero-kicker">India → Singapore &amp; Malaysia</div>
          <h1>Everything you need to sell well on ShipIndia.</h1>
          <p>
            {total} written lessons, four working tools and the checklists we use ourselves.
            Start with the guided path — it takes you from documents to your first promotion.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <button type="button" className="vu-btn" onClick={() => navigate('/path')}>
              Start the guided path
            </button>
            <button type="button" className="vu-btn vu-btn-onink" onClick={() => navigate('/checklists')}>
              Get the checklists
            </button>
          </div>
        </div>
        <div className="vu-hero-aside">
          <Ring pct={pct} />
          <div className="vu-ring-label">{read} of {total} lessons read</div>
        </div>
      </section>

      <div className="vu-grid" style={{ gridTemplateColumns: 'minmax(0,1.15fr) minmax(0,1fr)', marginTop: 'var(--space-6)' }}>
        {next && (
          <button
            type="button"
            className="vu-card vu-card-hover"
            style={{ textAlign: 'left', background: 'var(--orange-50)', borderColor: 'var(--orange-100)', cursor: 'pointer' }}
            onClick={() => navigate(`/track/${next.track.id}/${next.lesson.id}`)}
          >
            <div className="vu-kicker">{read ? 'Pick up where you left off' : 'First lesson'}</div>
            <div className="vu-h2" style={{ fontSize: 'var(--fs-h3)' }}>{next.lesson.title}</div>
            <p className="vu-muted" style={{ margin: 'var(--space-3) 0 var(--space-4)' }}>{next.lesson.summary}</p>
            <div style={{ color: 'var(--brand-primary)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-sm)' }}>
              {next.track.name} · {next.lesson.mins} min read →
            </div>
          </button>
        )}

        <div className="vu-card">
          <h2 className="vu-h3" style={{ fontSize: 'var(--fs-lg)', marginBottom: 'var(--space-2)' }}>
            The numbers we hold you to
          </h2>
          {SLAS.map((s) => (
            <div
              key={s.k}
              style={{
                display: 'flex', justifyContent: 'space-between', gap: 'var(--space-4)',
                padding: 'var(--space-3) 0', borderBottom: '1px solid var(--border-subtle)',
              }}
            >
              <span style={{ fontSize: 'var(--fs-sm)' }}>{s.k}</span>
              <span style={{ color: 'var(--brand-primary)', fontWeight: 'var(--fw-bold)', whiteSpace: 'nowrap' }}>
                {s.v}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="vu-sectionhead">
        <h2 className="vu-h2">The reference library</h2>
        <span className="vu-subtle">{content.tracks.length} tracks · {total} lessons</span>
      </div>

      <div className="vu-grid vu-grid-3">
        {content.tracks.map((track) => {
          const done = track.lessons.filter((l) => progress.done[lessonKey(track.id, l.id)]).length;
          const complete = done === track.lessons.length && track.lessons.length > 0;
          return (
            <Link key={track.id} to={`/track/${track.id}`} className="vu-card vu-card-hover">
              <span className="vu-icon-tile" style={{ background: track.tint }}>
                <Icon path={track.icon} size={20} color="var(--brand-primary)" />
              </span>
              <div className="vu-h3" style={{ fontSize: 'var(--fs-lg)', margin: 'var(--space-4) 0 var(--space-2)' }}>
                {track.name}
              </div>
              <p className="vu-muted" style={{ margin: 0, fontSize: 'var(--fs-sm)', lineHeight: 1.55 }}>
                {track.blurb}
              </p>
              <div className="vu-cardfoot">
                <span>{track.lessons.length} lessons · {trackMinutes(track)} min</span>
                <span style={complete ? { color: 'var(--green-500)', fontWeight: 600 } : undefined}>
                  {complete ? 'Complete' : `${done} read`}
                </span>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="vu-sectionhead">
        <h2 className="vu-h2">Tools that do the work with you</h2>
      </div>

      <div className="vu-grid vu-grid-4">
        {TOOL_CARDS.map((tool) => (
          <Link
            key={tool.to}
            to={tool.to}
            className="vu-card vu-card-hover"
            style={{ background: 'var(--orange-50)', borderColor: 'var(--orange-100)' }}
          >
            <Icon path={tool.path} size={22} color="var(--teal-400)" />
            <div className="vu-h3" style={{ fontSize: 'var(--fs-base)', margin: 'var(--space-3) 0 var(--space-2)' }}>
              {tool.name}
            </div>
            <p className="vu-muted" style={{ margin: 0, fontSize: 'var(--fs-sm)', lineHeight: 1.55 }}>
              {tool.blurb}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}
